import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useEffect, useState } from "react";
import type { ParitaWorkspace } from "@/common/user";
import { ReportStateReducerActionType } from "@/common/reportDesignerStateReducer";
import { ArrowRight } from "lucide-react";
import { getCustomInstanceMetadata } from "@/lib/serverComm";
import { INITIAL_REPORT_CONFIGURATIONS } from "@/common/report-configuration";

interface SPCustomProps {
  workspace: ParitaWorkspace;
  dispatch: React.Dispatch<ReportStateReducerActionType>;
}

interface ShowPalaceMetadata {
  regionCount: number;
  districts: string[];
}

const DATE_FILTER = {
  filterDefinitionName: "DATE",
  operator: "LAST_30_DAYS",
  value: "",
};

function ShoePalaceCustomPanel({ workspace, dispatch }: SPCustomProps) {
  void workspace;

  const [isLoaded, setIsLoaded] = useState(false);
  const [districts, setDistricts] = useState<string[]>([]);
  const [districtCount, setDistrictCount] = useState(0);
  const [regionCount, setRegionCount] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const showPalaceMetadata: ShowPalaceMetadata =
          await getCustomInstanceMetadata();
        setDistricts(showPalaceMetadata.districts);
        setDistrictCount(showPalaceMetadata.districts.length);
        setRegionCount(showPalaceMetadata.regionCount);
      } finally {
        setIsLoaded(true);
      }
    })();
  }, []);

  const loadAttritionByRegion = () => {
    const config = INITIAL_REPORT_CONFIGURATIONS.main,
      attritionConfig = {
        ...config,
        data: {
          ...config.data,
          measures: ["ATTRITION_RATE_BY_SEGMENT"],
          dimensions: ["REGION"],
          filters: [
            DATE_FILTER,
            {
              filterDefinitionName: "REGION",
              operator: "NOT_EQUALS",
              value: ["Non-Retail"],
            },
          ],
        },
      };

    dispatch({
      type: "setReportConfig",
      payload: attritionConfig,
    });
  };

  const loadAttritionByDistrict = () => {
    const config = INITIAL_REPORT_CONFIGURATIONS.main,
      attritionConfig = {
        ...config,
        data: {
          ...config.data,
          measures: ["ATTRITION_RATE_BY_SEGMENT"],
          dimensions: ["LOCATION"],
          filters: [
            DATE_FILTER,
            {
              filterDefinitionName: "LOCATION",
              operator: "NOT_EQUALS",
              value: ["Non-Retail"],
            },
          ],
        },
        chart: {
          ...config.chart,
          chartType: "Horizontal Bar",
          limit: 25,
        },
      };

    dispatch({
      type: "setReportConfig",
      payload: attritionConfig,
    });
  };

  const handleDistrictClick = (district: string) => {
    const config = INITIAL_REPORT_CONFIGURATIONS.main,
      attritionConfig = {
        ...config,
        data: {
          ...config.data,
          measures: ["ATTRITION_RATE_BY_SEGMENT"],
          dimensions: ["DIVISION"],
          filters: [
            DATE_FILTER,
            {
              filterDefinitionName: "LOCATION",
              operator: "EQUALS",
              value: [district],
            },
            {
              filterDefinitionName: "DIVISION",
              operator: "NOT_EQUALS",
              value: ["Non-Retail"],
            },
          ],
        },
        chart: {
          ...config.chart,
          chartType: "Horizontal Bar",
          limit: 25,
        },
        title: `Cohort-level Attrition Rate by Store in ${district}`,
        _titleManuallySet: true,
      };

    dispatch({
      type: "setReportConfig",
      payload: attritionConfig,
    });
  };

  if (!isLoaded) {
    return (
      <Card className="w-full border border-slate-300 bg-white shadow-none">
        <CardContent className="p-6">
          <div className="space-y-4 animate-pulse">
            <div className="h-5 w-40 rounded bg-slate-200" />
            <div className="h-4 w-72 rounded bg-slate-200" />
            <div className="grid gap-4 md:grid-cols-3">
              {[...Array(3)].map((_, index) => (
                <div
                  key={index}
                  className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4"
                >
                  <div className="h-6 w-28 rounded bg-slate-200" />
                  <div className="h-4 w-full rounded bg-slate-200" />
                  <div className="h-4 w-5/6 rounded bg-slate-200" />
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full border border-border bg-card shadow-slide-card">
      <CardContent className="p-0">
        <div className="px-5 pb-3 pt-5">
          <h3 className="text-lg font-semibold text-foreground">
            Attrition Quick Starts
          </h3>
        </div>
        <div className="overflow-hidden rounded-lg border border-border">
          <button
            type="button"
            className="group flex w-full items-center justify-between border-b border-border bg-muted/50 px-5 py-5 text-left transition-colors hover:bg-muted"
            onClick={loadAttritionByRegion}
          >
            <div className="flex items-center gap-4">
              <div className="h-16 w-[4px] rounded-full bg-primary" />
              <div>
                <div className="text-[1.05rem] font-semibold text-foreground">
                  Regional View
                </div>
                <div className="mt-1 text-[0.95rem] text-muted-foreground">
                  All retail workers broken down by region.
                </div>
              </div>
            </div>

            <span className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1 text-[0.7rem] font-semibold uppercase tracking-[0.08em] text-foreground">
              {`${regionCount} REGIONS`}
              <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
            </span>
          </button>

          <button
            type="button"
            className="group flex w-full items-center justify-between border-b border-border bg-muted/50 px-5 py-5 text-left transition-colors hover:bg-muted"
            onClick={loadAttritionByDistrict}
          >
            <div className="flex items-center gap-4">
              <div className="h-16 w-[4px] rounded-full bg-primary" />
              <div>
                <div className="text-[1.05rem] font-semibold text-foreground">
                  District-Level View
                </div>
                <div className="mt-1 text-[0.95rem] text-muted-foreground">
                  All retail workers broken down by district.
                </div>
              </div>
            </div>

            <span className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1 text-[0.7rem] font-semibold uppercase tracking-[0.08em] text-foreground">
              {`${districtCount} DISTRICTS`}
              <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
            </span>
          </button>

          <Accordion
            type="single"
            collapsible
            defaultValue="store-level"
            className="bg-muted/50"
          >
            <AccordionItem value="store-level" className="border-0">
              <AccordionTrigger className="px-5 py-5 text-left hover:no-underline [&>svg]:text-muted-foreground">
                <div className="flex w-full items-center justify-between gap-4 pr-2">
                  <div className="flex items-center gap-4">
                    <div className="h-16 w-[4px] rounded-full bg-primary" />
                    <div>
                      <div className="text-[1.05rem] font-semibold text-foreground">
                        Store-Level View
                      </div>
                      <div className="mt-1 text-[0.95rem] text-muted-foreground">
                        Select a district to see its stores.
                      </div>
                    </div>
                  </div>
                </div>
              </AccordionTrigger>

              <AccordionContent className="px-5 pb-5 pt-0">
                <div className="grid gap-6 md:grid-cols-3">
                  {Array.from({ length: 3 }, (_, columnIndex) => (
                    <div key={columnIndex} className="space-y-2">
                      {districts
                        .slice(columnIndex * 7, columnIndex * 7 + 7)
                        .map((district) => (
                          <div
                            key={`${district}-${columnIndex}`}
                            className="cursor-pointer text-[0.95rem] text-foreground hover:text-primary"
                            onClick={() => handleDistrictClick(district)}
                          >
                            {district.replace(/^District /, "")}
                          </div>
                        ))}
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </CardContent>
    </Card>
  );
}

export default ShoePalaceCustomPanel;
